import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Shield, Lock, Radio, ChevronRight, Volume2, VolumeX, Sun, Moon } from 'lucide-react';

// Sound
const AudioCtx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
let audioCtx = null;
const playSound = (type) => {
  try {
    if (!audioCtx && AudioCtx) audioCtx = new AudioCtx();
    if (!audioCtx) return;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain); gain.connect(audioCtx.destination);
    if (type === 'tick') { osc.type = 'sine'; osc.frequency.setValueAtTime(1200, audioCtx.currentTime); gain.gain.setValueAtTime(0.02, audioCtx.currentTime); gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.04); osc.start(); osc.stop(audioCtx.currentTime + 0.04); }
    else if (type === 'hover') { osc.frequency.setValueAtTime(600, audioCtx.currentTime); gain.gain.setValueAtTime(0.03, audioCtx.currentTime); gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.06); osc.start(); osc.stop(audioCtx.currentTime + 0.06); }
    else if (type === 'alert') { osc.type = 'square'; osc.frequency.setValueAtTime(440, audioCtx.currentTime); osc.frequency.setValueAtTime(880, audioCtx.currentTime + 0.1); gain.gain.setValueAtTime(0.06, audioCtx.currentTime); gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3); osc.start(); osc.stop(audioCtx.currentTime + 0.3); }
  } catch(e) {}
};

// Animated digit — Operative (scramble)
const ScrambleDigit = ({ value, label, soundOn }) => {
  const [display, setDisplay] = useState(value);
  const prevVal = useRef(value);
  useEffect(() => {
    if (prevVal.current !== value) {
      prevVal.current = value;
      let count = 0;
      const scramble = setInterval(() => {
        setDisplay(String(Math.floor(Math.random() * 100)).padStart(2, '0'));
        count++;
        if (count > 4) { clearInterval(scramble); setDisplay(value); if (soundOn) playSound('tick'); }
      }, 50);
      return () => clearInterval(scramble);
    } else { setDisplay(value); }
  }, [value, soundOn]);
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[72px] sm:w-[90px] h-[80px] sm:h-[100px] rounded-lg border border-cyan-500/20 flex items-center justify-center overflow-hidden" style={{ background: 'rgba(0,20,40,0.8)', boxShadow: '0 0 20px rgba(0,200,255,0.06), inset 0 0 30px rgba(0,0,0,0.5)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,100,0.03) 2px, rgba(0,255,100,0.03) 4px)' }} />
        <span className="text-3xl sm:text-4xl font-mono font-black text-[#00FF64] relative z-10" style={{ textShadow: '0 0 15px rgba(0,255,100,0.5), 0 0 30px rgba(0,255,100,0.2)' }}>{display}</span>
      </div>
      <span className="text-[10px] sm:text-xs text-gray-500 font-mono tracking-[0.2em] mt-2 uppercase">{label}</span>
    </div>
  );
};

// Animated digit — Executive (elegant flip)
const ExecDigit = ({ value, label }) => {
  const [display, setDisplay] = useState(value);
  const [flipping, setFlipping] = useState(false);
  const prevVal = useRef(value);
  useEffect(() => {
    if (prevVal.current !== value) {
      prevVal.current = value;
      setFlipping(true);
      setTimeout(() => { setDisplay(value); setFlipping(false); }, 200);
    } else { setDisplay(value); }
  }, [value]);
  return (
    <div className="flex flex-col items-center">
      <div className={`relative w-[68px] sm:w-[88px] h-[76px] sm:h-[96px] border border-[#E0DCD4] flex items-center justify-center transition-transform duration-200 ${flipping ? 'scale-95 opacity-70' : 'scale-100 opacity-100'}`} style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
        <span className="text-3xl sm:text-4xl font-bold text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif" }}>{display}</span>
      </div>
      <span className="text-[10px] sm:text-xs text-[#999] tracking-[0.15em] mt-2 uppercase" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>{label}</span>
    </div>
  );
};

// Scan line — Operative only
const ScanLine = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-20 opacity-30">
    <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF64] to-transparent absolute" style={{ animation: 'scanDown 4s linear infinite' }} />
  </div>
);

// Typing text
const TypeWriter = ({ text, speed = 40 }) => {
  const [displayed, setDisplayed] = useState('');
  const [cursor, setCursor] = useState(true);
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => { if (i < text.length) { setDisplayed(text.slice(0, i + 1)); i++; } else clearInterval(interval); }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  useEffect(() => { const blink = setInterval(() => setCursor(c => !c), 500); return () => clearInterval(blink); }, []);
  return <span>{displayed}<span className={`${cursor ? 'opacity-100' : 'opacity-0'} transition-opacity`}>_</span></span>;
};

// Executive fade-in text
const FadeInText = ({ text, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return <span className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`} style={{ display: 'inline-block' }}>{text}</span>;
};

export default function ComingSoon() {
  const [isExec, setIsExec] = useState(() => localStorage.getItem('sz_wl_mode') === 'executive');
  const [soundOn, setSoundOn] = useState(false);
  const [time, setTime] = useState({ d: '00', h: '00', m: '00', s: '00' });
  const [bootPhase, setBootPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showCTA, setShowCTA] = useState(false);
  const [execReady, setExecReady] = useState(false);

  const toggleMode = () => {
    const next = !isExec;
    setIsExec(next);
    localStorage.setItem('sz_wl_mode', next ? 'executive' : 'operative');
    if (!next && soundOn) playSound('hover');
  };

  // Boot sequence (Operative)
  useEffect(() => {
    const phases = [800, 1600, 2400, 3200, 4000];
    phases.forEach((delay, i) => { setTimeout(() => setBootPhase(i + 1), delay); });
    let p = 0;
    const pInterval = setInterval(() => { p += Math.random() * 3; if (p > 87) { p = 87; clearInterval(pInterval); } setProgress(Math.floor(p)); }, 100);
    setTimeout(() => setShowCTA(true), 4500);
    setTimeout(() => setExecReady(true), 1500);
    return () => clearInterval(pInterval);
  }, []);

  // Countdown
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const target = new Date(now);
      target.setDate(target.getDate() + 14);
      target.setHours(0, 0, 0, 0);
      const diff = target - now;
      setTime({
        d: String(Math.floor(diff / 86400000)).padStart(2, '0'),
        h: String(Math.floor((diff % 86400000) / 3600000)).padStart(2, '0'),
        m: String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0'),
        s: String(Math.floor((diff % 60000) / 1000)).padStart(2, '0'),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const bootMessages = [
    'ESTABLISHING SECURE CONNECTION...',
    'INITIALIZING THREAT DETECTION MATRIX...',
    'LOADING AI SUBSCRIPTION SCANNER...',
    'CALIBRATING KILL SWITCH PROTOCOLS...',
    'STANDING BY FOR DEPLOYMENT ORDERS...',
  ];

  const execSteps = [
    { text: 'Verifying security protocols', done: bootPhase >= 1 },
    { text: 'Preparing your financial shield', done: bootPhase >= 2 },
    { text: 'Configuring AI advisory suite', done: bootPhase >= 3 },
    { text: 'Finalizing your experience', done: bootPhase >= 4 },
  ];

  // ───── EXECUTIVE MODE ─────
  if (isExec) return (
    <div className="min-h-screen text-[#2C2C2C] relative overflow-hidden flex flex-col" style={{ background: '#F9F9F7' }} data-testid="coming-soon-page">
      {/* Subtle texture */}
      <div className="fixed inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #C0C0C0 0.5px, transparent 0)', backgroundSize: '40px 40px' }} />

      {/* Mode toggle */}
      <button onClick={toggleMode} className="fixed top-4 right-4 z-50 w-10 h-10 border border-[#E0DCD4] flex items-center justify-center text-[#999] hover:text-[#2C2C2C] transition-colors bg-white/80 backdrop-blur-sm" data-testid="coming-soon-mode-toggle" title="Switch to Operative Mode">
        <Moon className="w-4 h-4" />
      </button>

      {/* Header */}
      <nav className="relative z-30 px-6 py-5 flex items-center justify-between max-w-6xl mx-auto w-full">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 border-2 border-[#C0C0C0] flex items-center justify-center">
            <span className="text-lg font-bold text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>S</span>
          </div>
          <span className="font-bold text-lg tracking-tight text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Sub-Zero</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#BBB] tracking-[0.15em] uppercase" style={{ fontFamily: "Arial, sans-serif" }}>Private Beta</span>
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-30 -mt-4">
        {/* Status */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 border border-[#E0DCD4] mb-8 transition-all duration-1000 ${execReady ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-2 h-2 rounded-full bg-[#2C2C2C]" />
          <span className="text-xs text-[#999] tracking-[0.15em] uppercase" style={{ fontFamily: "Arial, sans-serif" }}>Launching Soon</span>
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-4 leading-[1.1]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          <FadeInText text="Your Money Deserves" delay={300} />
          <br />
          <FadeInText text="A Guardian." delay={900} />
        </h1>
        <p className={`text-center text-[#888] text-base sm:text-lg max-w-md mx-auto mb-10 leading-relaxed transition-all duration-1000 delay-[1500ms] ${execReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ fontFamily: "Arial, sans-serif" }}>
          The average person loses $412 a year to forgotten subscriptions, silent price hikes, and trial traps. We built something that fights back — quietly, intelligently, and relentlessly.
        </p>

        {/* Countdown */}
        <div className="flex items-center gap-3 sm:gap-5 mb-10">
          <ExecDigit value={time.d} label="Days" />
          <span className="text-2xl text-[#D0D0D0] mt-[-20px]" style={{ fontFamily: "Georgia, serif" }}>:</span>
          <ExecDigit value={time.h} label="Hours" />
          <span className="text-2xl text-[#D0D0D0] mt-[-20px]" style={{ fontFamily: "Georgia, serif" }}>:</span>
          <ExecDigit value={time.m} label="Minutes" />
          <span className="text-2xl text-[#D0D0D0] mt-[-20px]" style={{ fontFamily: "Georgia, serif" }}>:</span>
          <ExecDigit value={time.s} label="Seconds" />
        </div>

        {/* Preparation steps */}
        <div className="w-full max-w-md mb-10">
          <div className="border border-[#E0DCD4] overflow-hidden" style={{ background: '#FFFFFF' }}>
            <div className="px-5 py-3 border-b border-[#E8E4E0] flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full transition-colors duration-500 ${bootPhase >= 4 ? 'bg-[#2C2C2C]' : 'bg-[#C0C0C0] animate-pulse'}`} />
              <span className="text-[10px] text-[#999] tracking-[0.15em] uppercase" style={{ fontFamily: "Arial, sans-serif" }}>Preparing Your Experience</span>
            </div>
            <div className="px-5 py-4 space-y-3">
              {execSteps.map((step, i) => (
                <div key={i} className={`flex items-center gap-3 transition-all duration-700 ${step.done ? 'opacity-100' : 'opacity-0 translate-y-2'}`}>
                  <div className={`w-5 h-5 border flex items-center justify-center text-xs transition-colors duration-300 ${step.done ? 'border-[#2C2C2C] bg-[#2C2C2C] text-white' : 'border-[#E0DCD4] text-transparent'}`}>
                    {step.done && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span className={`text-sm transition-colors duration-300 ${step.done ? 'text-[#555]' : 'text-[#CCC]'}`} style={{ fontFamily: "Arial, sans-serif" }}>{step.text}</span>
                </div>
              ))}
            </div>
            {/* Progress */}
            <div className="px-5 pb-4">
              <div className="h-1 bg-[#F0EDE8] overflow-hidden">
                <div className="h-full bg-[#2C2C2C] transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className={`transition-all duration-1000 ${showCTA ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-6">
            <p className="text-[#999] text-xs tracking-[0.1em] uppercase" style={{ fontFamily: "Arial, sans-serif" }}>
              <FadeInText text="Early access is now open for select members" delay={4800} />
            </p>
          </div>

          <Link to="/waitlist" className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#111] text-white font-bold tracking-wide text-sm hover:bg-[#2C2C2C] transition-all hover:scale-[1.02] active:scale-[0.98]" data-testid="coming-soon-waitlist-cta" style={{ fontFamily: "Arial, sans-serif" }}>
            <Shield className="w-5 h-5" />
            <span>Secure Your Position</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>

          <p className="text-center text-[#BBB] text-xs mt-4 max-w-sm mx-auto leading-relaxed" style={{ fontFamily: "Arial, sans-serif" }}>
            Join 300+ members who've chosen to take control of their financial future. Priority access for early members.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-30 px-6 py-6 text-center">
        <p className="text-xs text-[#BBB]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Sub-Zero</p>
        <p className="text-[10px] text-[#CCC] mt-1" style={{ fontFamily: "Arial, sans-serif" }}>Your AI Financial Guardian. Launching Soon.</p>
      </footer>
    </div>
  );

  // ───── OPERATIVE MODE ─────
  return (
    <div className="min-h-screen bg-[#030508] text-white relative overflow-hidden flex flex-col" data-testid="coming-soon-page">
      <div className="fixed inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(0,255,100,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,100,0.3) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
      <div className="fixed inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,255,100,0.3) 1px, transparent 0)', backgroundSize: '20px 20px' }} />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, rgba(0,200,255,0.08) 0%, transparent 70%)' }} />
      <ScanLine />

      {/* Mode + Sound toggles */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button onClick={toggleMode} className="w-10 h-10 rounded-full border border-cyan-500/20 flex items-center justify-center text-cyan-400/60 hover:text-[#00FF64] transition-colors bg-black/50 backdrop-blur-sm" data-testid="coming-soon-mode-toggle" title="Switch to Executive Mode">
          <Sun className="w-4 h-4" />
        </button>
        <button onClick={() => setSoundOn(!soundOn)} className="w-10 h-10 rounded-full border border-cyan-500/20 flex items-center justify-center text-cyan-400/60 hover:text-[#00FF64] transition-colors bg-black/50 backdrop-blur-sm" data-testid="coming-soon-sound">
          {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>

      {/* Header */}
      <nav className="relative z-30 px-6 py-5 flex items-center justify-between max-w-6xl mx-auto w-full">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#00FF64]/10 border border-[#00FF64]/30 flex items-center justify-center">
            <Zap className="w-5 h-5 text-[#00FF64]" />
          </div>
          <span className="font-bold text-lg tracking-tight font-mono text-white">SUB-ZERO</span>
        </div>
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-cyan-500/40" />
          <span className="text-xs text-cyan-500/40 font-mono tracking-wider hidden sm:inline">CLASSIFIED</span>
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-30 -mt-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/15 bg-cyan-500/5 mb-8" style={{ animation: 'pulse 3s ease-in-out infinite' }}>
          <Radio className="w-3.5 h-3.5 text-[#00FF64] animate-pulse" />
          <span className="text-[#00FF64] text-xs font-mono tracking-[0.2em]">SYSTEM INITIALIZATION IN PROGRESS</span>
        </div>

        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-center mb-3 tracking-tight leading-[1.05]">
          <span className="relative inline-block">
            <span className="relative z-10 text-white">SOMETHING</span>
            <span className="absolute top-0 left-0 text-red-500/50" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 33%, 0 33%)', transform: 'translate(2px, -1px)', animation: 'glitch1 3s infinite' }}>SOMETHING</span>
            <span className="absolute top-0 left-0 text-cyan-400/50" style={{ clipPath: 'polygon(0 67%, 100% 67%, 100% 100%, 0 100%)', transform: 'translate(-2px, 1px)', animation: 'glitch2 3s infinite 0.1s' }}>SOMETHING</span>
          </span>
        </h1>
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-center mb-10 tracking-tight">
          <span className="text-[#00FF64]" style={{ textShadow: '0 0 30px rgba(0,255,100,0.3), 0 0 60px rgba(0,255,100,0.1)' }}>IS COMING.</span>
        </h1>

        {/* Countdown */}
        <div className="flex items-center gap-3 sm:gap-5 mb-10">
          <ScrambleDigit value={time.d} label="Days" soundOn={soundOn} />
          <span className="text-2xl text-cyan-500/30 font-mono mt-[-20px]">:</span>
          <ScrambleDigit value={time.h} label="Hours" soundOn={soundOn} />
          <span className="text-2xl text-cyan-500/30 font-mono mt-[-20px]">:</span>
          <ScrambleDigit value={time.m} label="Minutes" soundOn={soundOn} />
          <span className="text-2xl text-cyan-500/30 font-mono mt-[-20px]">:</span>
          <ScrambleDigit value={time.s} label="Seconds" soundOn={soundOn} />
        </div>

        {/* Boot terminal */}
        <div className="w-full max-w-lg mb-10">
          <div className="rounded-xl border border-cyan-500/10 overflow-hidden" style={{ background: 'rgba(0,10,20,0.8)', boxShadow: '0 0 30px rgba(0,200,255,0.04)' }}>
            <div className="px-4 py-2 border-b border-cyan-500/10 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF64] animate-pulse" />
              <span className="text-[10px] text-cyan-500/40 font-mono tracking-wider">SYSTEM BOOT SEQUENCE</span>
            </div>
            <div className="px-4 py-3 space-y-1.5 font-mono text-xs">
              {bootMessages.map((msg, i) => (
                <div key={i} className={`flex items-center gap-2 transition-all duration-500 ${bootPhase > i ? 'opacity-100' : 'opacity-0 translate-y-2'}`}>
                  <span className={bootPhase > i ? 'text-[#00FF64]' : 'text-gray-600'}>{bootPhase > i ? '[OK]' : '[..]'}</span>
                  <span className={bootPhase > i ? 'text-gray-400' : 'text-gray-700'}>{msg}</span>
                </div>
              ))}
            </div>
            <div className="px-4 pb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-cyan-500/30 font-mono">DEPLOYMENT PROGRESS</span>
                <span className="text-[10px] text-[#00FF64] font-mono">{progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#00FF64] to-cyan-400 transition-all duration-300" style={{ width: `${progress}%`, boxShadow: '0 0 10px rgba(0,255,100,0.4)' }} />
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className={`transition-all duration-1000 ${showCTA ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-6">
            <p className="text-gray-500 text-xs font-mono tracking-[0.15em] mb-2">
              <TypeWriter text="CLASSIFIED INTEL: EARLY ACCESS MISSION AVAILABLE" speed={30} />
            </p>
          </div>
          <Link to="/waitlist" className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-[#00FF64] text-black font-bold font-mono tracking-wide text-sm hover:shadow-[0_0_40px_rgba(0,255,100,0.4)] transition-all hover:scale-105 active:scale-95" onMouseEnter={() => soundOn && playSound('alert')} data-testid="coming-soon-waitlist-cta">
            <Shield className="w-5 h-5" />
            <span>SECURE YOUR SLOT — JOIN THE MISSION</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: '0 0 30px rgba(0,255,100,0.3)' }} />
          </Link>
          <p className="text-center text-gray-600 text-xs font-mono mt-4 max-w-sm mx-auto">
            Agents are being recruited now. Early operatives get priority deployment + bonus XP.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-30 px-6 py-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Zap className="w-3 h-3 text-[#00FF64]/40" />
          <span className="text-xs text-gray-600 font-mono">SUB-ZERO OPS</span>
        </div>
        <p className="text-[10px] text-gray-700 font-mono">Your AI Financial Bodyguard. Deployment Imminent.</p>
      </footer>

      <style>{`
        @keyframes scanDown { 0% { top: -2px; } 100% { top: 100%; } }
        @keyframes glitch1 { 0%, 100% { transform: translate(0); } 20% { transform: translate(3px, -2px); } 40% { transform: translate(-3px, 2px); } 60% { transform: translate(2px, 1px); } 80% { transform: translate(-2px, -1px); } }
        @keyframes glitch2 { 0%, 100% { transform: translate(0); } 25% { transform: translate(-2px, 2px); } 50% { transform: translate(3px, -1px); } 75% { transform: translate(-1px, 1px); } }
      `}</style>
    </div>
  );
}
